/**
 * app.js
 *
 * This is the entry file for the application, only setup and boilerplate
 * code.
 */
import 'utils/wdyr'; // instruments/wraps react code, so should always be the first import before render started
// import 'utils/selectorsTracker';

import "core-js/stable";
import "regenerator-runtime/runtime";

import store from 'store';
import {setStore} from 'utils/getStore';

// Import all the third party stuff
import React from 'react';
import ReactDOM from 'react-dom';
import {Provider} from 'react-redux';
import {ConnectedRouter} from 'connected-react-router/immutable';
import {persistStore, getPersistor} from 'persistor';
import {PersistGate} from 'redux-persist/integration/react';
import 'sanitize.css/sanitize.css';
import {StylesProvider, jssPreset} from '@material-ui/core/styles';

import {create as jssCreate} from 'jss';
import jssCamelCase from 'jss-plugin-camel-case';
import jssDefaultUnit from 'jss-plugin-default-unit';
import jssGlobal from 'jss-plugin-global';
import jssExtend from 'jss-plugin-extend';
import jssNested from 'jss-plugin-nested';
import jssPluginTemplate from 'jss-plugin-template';
import jssPropsSort from 'jss-plugin-props-sort';
import jssRuleValueFunction from 'jss-plugin-rule-value-function';
import jssVendorPrefixer from 'jss-plugin-vendor-prefixer';

import {MuiPickersUtilsProvider} from '@material-ui/pickers';
import LuxonUtils from '@date-io/luxon';
import * as am4core from '@amcharts/amcharts4/core';

import App from 'containers/App';
import { appSetOnlineStatus } from 'data/app/actions';

import { initializeFeatureFlags } from 'utils/featureFlags/initialize';
import history from 'utils/history';
import {getLogger} from 'utils/logger';
import tracker from 'utils/tracker';
import {dispatchEscapeKeyEvent} from 'utils/utils';
import ErrorBoundary from 'components/ErrorBoundary';
import PersistLoading from 'components/PersistLoading';
import consoleMirror from 'utils/consoleMirror';
import {setupAssert, setupVerify} from 'utils/assert';

// Load the favicon and the .htaccess file
/////////////////////////////////////
///////////////////////////////////////////////////////////////////////
/////////////////////////////////////////////////////////////////////////////////////////
/////////
////////////////////////////////////////
import '!file-loader?name=favicon.[ext]!./images/favicon-quicken.ico';
import '!file-loader?name=apple-touch-icon.[ext]!./images/apple-touch-icon-quicken.png';
/////////
import 'file-loader?name=[name].[ext]!./.htaccess'; // eslint-disable-line import/extensions

import GlobalStyles from './global-styles';
// TODO: Import root routes
import createSagas from './sagas';

// TODO: Import selector for `syncHistoryWithStore`
// import { makeSelectLocationState } from 'containers/App/selectors';
/* eslint-enable import/no-webpack-loader-syntax */

// ensure that first thing is store setup
setStore(store);

tracker.track(tracker.events.init);

consoleMirror.attach();
setupAssert();
setupVerify();

persistStore(store);

const isIe = require('is-iexplorer');
const log = getLogger('app.js');

function nodeListPolyfill() {
  if (typeof NodeList.prototype.forEach === 'function') return false;
  NodeList.prototype.forEach = Array.prototype.forEach;
  return true;
}

nodeListPolyfill();

// ===============================================================================================
// Setup JSS Plugins (@material-ui/core requires certain JSS Plugins and we want to add to those)
// ===============================================================================================

// Note: This needs to include the plugins required by @material-ui/core. @material-ui/core provides a
// jssPreset().plugins array to use. THe implied usage would be:
//   const jss = jssCreate({ plugins: [jssExtend(), ...jssPreset().plugins] })
// However, this doesn't work correctly because the order of plugins matter. Therefore, will
// manually maintain the list of plugins required (including those required by @material-ui/core. This
// may cause issues in the future if the list required by @material-ui/core changes.

if (jssPreset().plugins.length !== 7) {
  log.warn('Check JSS Plugins, @material-ui/core Dependencies Changed');
}

// preset-defaults +compose, +expand, +rule-value-observable, +template. -extend

// Note: Be careful, order does matter!!
//
const jss = jssCreate({
  plugins: [
    jssRuleValueFunction(),
    jssPluginTemplate(),
    jssGlobal(),
    jssExtend(),
    jssNested(),
    jssCamelCase(),
    jssDefaultUnit(),
    jssVendorPrefixer(),
    jssPropsSort(),
  ],
});

// ===============================================================================================
// Initialize Launch Darkly (feature-flags)
// ===============================================================================================
initializeFeatureFlags();

// ===============================================================================================
// React APP Render
// ===============================================================================================

const MOUNT_NODE = document.getElementById('app');
const render = () => {
  const persisitor = getPersistor();
  ReactDOM.render(
    <Provider store={store}>
      <ConnectedRouter history={history}>
        <ErrorBoundary>
          <StylesProvider jss={jss}>
            <MuiPickersUtilsProvider utils={LuxonUtils}>
              {persisitor ?
                <PersistGate loading={<PersistLoading/>} persistor={persisitor}>
                  <App/>
                </PersistGate>
                :
                <App/>}
            </MuiPickersUtilsProvider>
          </StylesProvider>
          <GlobalStyles/>
        </ErrorBoundary>
      </ConnectedRouter>
    </Provider>,
    MOUNT_NODE
  );
};

// add global saga's
export const sagaTasks = createSagas(store);

if (module.hot) {
  // Hot reloadable React components and translation json files
  // modules.hot.accept does not accept dynamic dependencies,
  // have to be constants at compile-time
  module.hot.accept(['containers/App'], () => {
    ReactDOM.unmountComponentAtNode(MOUNT_NODE);
    render();
  });
}

render();

// eslint-disable-next-line no-constant-condition
// if (false && process.env.NODE_ENV !== 'production') {
//   const { whyDidYouUpdate } = require('why-did-you-update'); // eslint-disable-line
//   whyDidYouUpdate(React);
// }

window.onclick = () => {
  setTimeout(() => tracker.trackActive(), 1000);
};

//----------------------------------------------------------------------------------------------------------------------
// on resize, trigger an ESC keyup event to get MUI to close things it should before resize
//----------------------------------------------------------------------------------------------------------------------
function handleResize() {
  if (!isIe) dispatchEscapeKeyEvent(); // closes any open MUI things like Popovers
}

function resizeThrottler() {
  if (this.resizeTimeout) {
    clearTimeout(this.resizeTimeout);
  }
  this.resizeTimeout = setTimeout(handleResize, 66);
}

window.addEventListener('resize', resizeThrottler);

// enable amcharts license
am4core.options.commercialLicense = true;

// var installPromptEvent;
// window.addEventListener('beforeinstallprompt', function(event) {
//   event.preventDefault();
//   event.prompt();
//   installPromptEvent = event;
//   console.log('Got BeforeInstallPrompt event');
// });

window.addEventListener("load", () => {
  function handleNetworkChange(_event) {
    if (navigator.onLine) {
      log.debug('Application is online');
      store.dispatch(appSetOnlineStatus(true));
    } else {
      log.debug('Application is offline');
      store.dispatch(appSetOnlineStatus(false));
    }
  }
  window.addEventListener("online", handleNetworkChange);
  window.addEventListener("offline", handleNetworkChange);
});

if (!navigator.onLine) {
  log.debug('Application is offline 1', navigator.onLine, navigator);
  store.dispatch(appSetOnlineStatus(false));
}
